import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export default function VideoViewer({ videos }) {

	const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
	const selectedVideoRef = useRef(null);
	const touchStartX = useRef(0);
	const touchEndX = useRef(0);

	const nextVideo = () => {
		setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
	};

	const prevVideo = () => {
		setCurrentVideoIndex((prev) => (prev - 1 + videos.length) % videos.length);
	};

	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
	};

	const handleTouchMove = (e) => {
		touchEndX.current = e.touches[0].clientX;
	};

	const handleTouchEnd = () => {
		const swipeDistance = touchStartX.current - touchEndX.current;
		const minSwipeDistance = 50; // px threshold to count as a swipe

		if (swipeDistance > minSwipeDistance) {
			// Swiped left → next image
			nextVideo();
		} else if (swipeDistance < -minSwipeDistance) {
			// Swiped right → previous image
			nextVideo();
		}
	};

	useEffect(() => {
		if (selectedVideoRef.current) {
			selectedVideoRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "center", // centers it horizontally in the slider
			});
		}
	}, [currentVideoIndex]);

	return (
		<div>
			<motion.div
				initial={ { opacity: 0, x: -20 } }
				animate={ { opacity: 1, x: 0 } }
				transition={ { duration: 0.6 } }
				className="relative"
				onTouchStart={ handleTouchStart }
				onTouchMove={ handleTouchMove }
				onTouchEnd={ handleTouchEnd }
			>
				<div className="aspect-[16/10] bg-[#F2F0E9] rounded-sm overflow-hidden group relative mb-1">
					<video
						src={ videos[currentVideoIndex].preview }
						controls
						className="w-full h-full object-contain bg-black"
					/>
					{/* Navigation Arrows */ }
					{ videos.length > 1 && (
						<>
							<button
								onClick={ (e) => {
									e.stopPropagation();
									prevVideo();
								} }
								className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full
										flex items-center justify-center transition-colors"
								data-testid="prev-video-btn"
							>
								<ChevronLeft className="w-6 h-6" strokeWidth={ 1.5 }/>
							</button>
							<button
								onClick={ (e) => {
									e.stopPropagation();
									nextVideo();
								} }
								className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full
										flex items-center justify-center transition-colors"
								data-testid="next-video-btn"
							>
								<ChevronRight className="w-6 h-6" strokeWidth={ 1.5 }/>
							</button>
						</>
					) }
				</div>

				{/* Thumbnails */ }
				{ videos.length > 1 && (
					<div className="flex gap-2 overflow-x-auto pb-2">
						{ videos.map((vid, i) => (
							<div key={ i }
									 onClick={ () => setCurrentVideoIndex(i) }
									 className={ `flex-shrink-0 w-32 h-20 bg-gray-800 aspect-video rounded-sm overflow-hidden cursor-pointer 
									 		border-2 transition-colors ${
										 i === currentVideoIndex ? 'border-[#8B4513]' : 'border-transparent'
									 }` }>
								<video
									ref={ currentVideoIndex === i ? selectedVideoRef : null }
									src={ vid.preview }
									className="w-full h-full object-cover"
									muted
									preload="metadata" crossOrigin="anonymous"/>
							</div>
						)) }
					</div>
				) }
			</motion.div>
		</div>
	);
};